// Admin Reports Page - Review user reports and take action
import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../App";
import { ArrowLeft, RefreshCw, Flag, CheckCircle, XCircle, Search, AlertCircle } from "lucide-react";

const API = window.location.origin;

function StatusPill({ status }) {
  const s = (status || "open").toLowerCase();
  if (s === "open") return (
    <span className="text-xs px-3 py-1 rounded-full font-medium bg-rose-500/10 text-rose-400 border border-rose-500/30">
      🔴 Open
    </span>
  );
  if (s === "resolved") return (
    <span className="text-xs px-3 py-1 rounded-full font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
      ✅ Resolved
    </span>
  );
  return (
    <span className="text-xs px-3 py-1 rounded-full font-medium bg-slate-500/10 text-slate-400 border border-slate-500/30">
      ⏭️ Ignored
    </span>
  );
}

export default function AdminReportsPage() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("open");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const isAdmin = user?.is_admin;

  async function load() {
    if (!isAdmin) return;
    setBusy(true);
    setToast(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("limit", "50");
      const res = await fetch(`${API}/api/admin/reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to load reports");
      setItems(data.items || []);
      setTotal(data.items?.length || 0);
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    
  }, [status, isAdmin]);

  // Check admin access after hooks
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Admin access required</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <button
              onClick={() => navigate("/admin")}
              className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </button>
            <h1 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-rose-400 to-orange-500 bg-clip-text text-transparent">
              Reports
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Review user reports and take moderation action
            </p>
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50 transition"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
            {busy ? "Loading…" : "Refresh"}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center mb-6">
          <select
            className="rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm text-slate-200"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="open">🔴 Open</option>
            <option value="resolved">✅ Resolved</option>
            <option value="ignored">⏭️ Ignored</option>
            <option value="">All</option>
          </select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              className="rounded-xl border border-slate-700 bg-slate-800 pl-10 pr-4 py-2 text-sm text-slate-200 w-64 placeholder:text-slate-500"
              placeholder="Search email or user ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>

          <button
            onClick={load}
            disabled={busy}
            className="rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50 transition"
          >
            Search
          </button>

          <div className="ml-auto text-sm text-slate-400">
            Showing: <span className="font-medium text-slate-200">{total}</span>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`mb-4 rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
              toast.type === "error"
                ? "border-rose-500/30 bg-rose-500/10 text-rose-400"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
            }`}
          >
            {toast.type === "error" ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}

        {/* Reports List */}
        <div className="space-y-3">
          {items.map((r) => (
            <div
              key={r.id}
              onClick={() => navigate(`/admin/reports/${r.id}`)}
              className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 hover:border-slate-700 hover:bg-slate-800/50 transition cursor-pointer"
            >
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-rose-400" />
                    <span className="text-sm font-semibold text-slate-200">
                      {r.reason || r.type || "User Report"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                    <div>Reporter: <span className="text-slate-400">{r.reporter_email || r.reporter_user_id || "unknown"}</span></div>
                    <div>Reported: <span className="text-slate-400">{r.reported_email || r.reported_user_id || "unknown"}</span></div>
                    {r.created_at && (
                      <div>Created: <span className="text-slate-400">{new Date(r.created_at).toLocaleString()}</span></div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <StatusPill status={r.status} />
                </div>
              </div>

              {r.message && (
                <div className="mt-3 text-sm text-slate-400 line-clamp-2 bg-slate-800/50 rounded-xl p-3">
                  &ldquo;{r.message}&rdquo;
                </div>
              )}
            </div>
          ))}

          {items.length === 0 && !busy && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 text-center">
              <AlertCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
              <div className="text-slate-500 text-sm">
                {status === "open" ? "No open reports 🎉" : "No reports found for this filter."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
